import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';
import {
  getDefinitionByUIField,
  getDefinitionForSimpleFilter,
} from '@/sections/insights/queryBuilder/entities/utils/simpleFiltersUtils';

import { DropdownValueInput } from './DropdownValueInput';
import { FilterDeleteButton } from './FilterDeleteButton';
import { NumberValueInput } from './NumberValueInput';
import { TextValueInput } from './TextValueInput';

// Helper function to determine if operator supports multiple values
const supportsMultipleValues = (operator: string): boolean => {
  const multiValueOperators = ['is any of', 'is none of'];
  return multiValueOperators.includes(operator);
};

interface NodeFilterProps {
  simpleFilter: SimpleFilter;
  index: number;
  editable: boolean;
  filterDefinitions: SimpleFilterDefinition[] | null;
  onFilterChange: (index: number, updatedFilter: Partial<SimpleFilter>) => void;
  onFilterDelete: (index: number) => void;
}

export const NodeFilter = ({
  simpleFilter,
  index,
  editable,
  filterDefinitions,
  onFilterChange,
  onFilterDelete,
}: NodeFilterProps) => {
  // Get field definition from UI field name
  const definition = getDefinitionForSimpleFilter(
    filterDefinitions,
    simpleFilter
  );

  // Get API field name for select value (backwards compatibility with tests)
  const uiFieldName = definition?.uiField || simpleFilter.field;

  // Determine if current operator supports multiple values
  const allowMultiple = supportsMultipleValues(simpleFilter.operator);

  return (
    <div className="flex items-center gap-2">
      {/* Filter field dropdown */}
      <select
        disabled={!editable || !filterDefinitions}
        value={uiFieldName}
        onChange={e => {
          // Find the UI field name from the selected API field name
          const selectedUIField = e.target.value;
          const selectedDef = getDefinitionByUIField(
            filterDefinitions,
            selectedUIField
          );

          onFilterChange(index, {
            field: selectedUIField,
            // Use first operator from new field definition
            operator: selectedDef?.operators[0] || '',
            value: '',
          });
        }}
        className={`flex-[3] rounded border px-3 py-1.5 text-sm ${
          editable && filterDefinitions
            ? 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-brand-primary'
            : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary cursor-not-allowed opacity-90'
        }`}
        aria-label="Filter field"
      >
        {filterDefinitions ? (
          Array.from(filterDefinitions.values())
            .flat()
            .map(def => {
              return (
                <option key={def.uiField} value={def.uiField}>
                  {def.uiField}
                </option>
              );
            })
        ) : (
          <option value={uiFieldName}>{simpleFilter.field}</option>
        )}
      </select>
      {/* Operator dropdown */}
      <select
        disabled={!editable || !simpleFilter.field || !filterDefinitions}
        value={simpleFilter.operator}
        onChange={e =>
          onFilterChange(index, { operator: e.target.value, value: '' })
        }
        className={`flex-[3.5] rounded border px-3 py-1.5 text-sm ${
          editable && simpleFilter.field && filterDefinitions
            ? 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-brand-primary'
            : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary cursor-not-allowed opacity-90'
        }`}
        aria-label="Filter operator"
      >
        {definition ? (
          definition.operators.map(op => (
            <option key={op} value={op}>
              {op}
            </option>
          ))
        ) : (
          <option value={simpleFilter.operator}>{simpleFilter.operator}</option>
        )}
      </select>

      {/* Value dropdown/input based on inputType */}
      {definition ? (
        <>
          {definition.inputType === 'text' && (
            <TextValueInput
              value={simpleFilter.value}
              onChange={newValue => onFilterChange(index, { value: newValue })}
              disabled={!editable || !simpleFilter.field}
              filterDefinition={definition}
              allowMultiple={allowMultiple}
            />
          )}
          {definition.inputType === 'number' && (
            <NumberValueInput
              value={simpleFilter.value}
              onChange={newValue => onFilterChange(index, { value: newValue })}
              disabled={!editable || !simpleFilter.field}
              filterDefinition={definition}
              allowMultiple={allowMultiple}
            />
          )}
          {definition.inputType === 'dropdown' && (
            <DropdownValueInput
              value={simpleFilter.value}
              onChange={newValue => onFilterChange(index, { value: newValue })}
              disabled={!editable || !simpleFilter.field}
              filterDefinition={definition}
              allowMultiple={allowMultiple}
            />
          )}
        </>
      ) : (
        // Fallback for missing filterDefinitions
        <input
          type="text"
          readOnly
          value={
            Array.isArray(simpleFilter.value)
              ? simpleFilter.value.join(', ')
              : String(simpleFilter.value)
          }
          className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary flex-[3.5] cursor-not-allowed rounded border px-3 py-1.5 text-sm text-purple-700 opacity-90"
          aria-label="Filter value"
        />
      )}
      {/* Delete button */}
      {editable && (
        <FilterDeleteButton
          onDelete={() => onFilterDelete(index)}
          disabled={!editable}
          filterIndex={index}
        />
      )}
    </div>
  );
};
